import { coerceNumberProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
    CustomProtocolService,
    CustomStorageRegimeStage,
    Evaluation,
    EvaluationService,
    Sample,
    SampleType,
    ScheduledActionService,
    SiteFormDialogData,
    Status,
    StorageRegime,
    StorageRegimeService,
} from '@core/data';
import { Dialog, Snackbar } from '@core/material';
import { coerseDateProperty, NumberValidators } from '@core/utils';
import { Select, Store } from "@ngxs/store";
import { Protocol } from '@library';
import * as moment from 'moment';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { LoadSampleIndex } from '../sample-index/sample-index.state-actions';
import { switchMap, takeUntil } from 'rxjs/operators';
import { CultivarFormDialog, CultivarFormDialogData } from '../cultivar-form/cultivar-form.dialog';
import { SiteFormDialog } from '../site-form/site-form.dialog';
import { InitNewSampleForm, InitUpdateSampleForm, SampleFormState, SampleFormStateModel, SubmitSampleForm } from './sample-form.state';
import { evaluationsFromStorageRegime, scheduledActionsFromStorageRegime } from './storage-regime-schedule';

export interface SampleFormDialogData {
    key?: string;
    defaults?: any;
    clone?: boolean;
}

@Component({
    selector: 'pv-sample-form-dialog',
    templateUrl: 'sample-form.dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false
})
export class SampleFormDialog implements OnInit, OnDestroy {

    @Select(SampleFormState)
    state$: Observable<SampleFormStateModel>;

    @Select(SampleFormState.data)
    data$: Observable<Partial<Sample>>;

    typeControl = new FormControl(null, [Validators.required]);
    labelControl = new FormControl(null, [Validators.maxLength(64)]);
    descriptionControl = new FormControl(null, [Validators.maxLength(255)]);
    sizeControl = new FormControl(null, [NumberValidators.integer(4), Validators.min(1)]);

    siteControl = new FormControl(null);
    scionCultivarControl = new FormControl(null);
    rootstockCultivarControl = new FormControl(null);
    plantSampleControl = new FormControl(null);

    birthDateControl = new FormControl(null, [Validators.required]);
    deathDateControl = new FormControl(null, []);

    rowIndexControl = new FormControl(null, [NumberValidators.integer(3), Validators.min(0)]);
    positionIndexControl = new FormControl(null, [NumberValidators.integer(3), Validators.min(0)]);
    rowDistanceControl = new FormControl(null, [NumberValidators.integer(5), Validators.min(1)]);
    colDistanceControl = new FormControl(null, [NumberValidators.integer(5), Validators.min(1)]);

    ownerOrgKeyControl = new FormControl(null);

    connectPlantSampleControl = new FormControl(false);

    // Storage Regime selection at harvest extraction — optional,
    // only ever shown for a NEW, non-mass-action harvest Sample.
    storageRegimeControl = new FormControl(null);
    protocolControl = new FormControl(null);

    formGroup: FormGroup = new FormGroup({
        type: this.typeControl,
        label: this.labelControl,
        description: this.descriptionControl,
        size: this.sizeControl,

        site: this.siteControl,
        scionCultivar: this.scionCultivarControl,
        rootstockCultivar: this.rootstockCultivarControl,
        plantSample: this.plantSampleControl,

        birthDate: this.birthDateControl,
        deathDate: this.deathDateControl,

        rowIndex: this.rowIndexControl,
        positionIndex: this.positionIndexControl,
        rowDistance: this.rowDistanceControl,
        colDistance: this.colDistanceControl,

        ownerOrgKey: this.ownerOrgKeyControl,

        _connectPlantSample: this.connectPlantSampleControl,

        storageRegime: this.storageRegimeControl,
        protocolId: this.protocolControl,
    });

    // Populated once for a new, non-mass-action harvest Sample —
    // not NGXS state, since this dropdown's data has no reason to persist
    // beyond this dialog instance.
    storageRegimeOptions: StorageRegime[] = [];
    protocolOptions: Protocol[] = [];

    // The regime and protocol lists follow the chosen cultivar's crop, so a regime can only be picked once a cultivar is.
    private _orgKey: string = null;
    private _loadedCropId: string | null = null;
    private _scheduling = false;
    private _scheduledRegime: StorageRegime | null = null;
    private _scheduledProtocolId: string | null = null;
    private _optionsRequests: Subscription[] = [];

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _changeDetector: ChangeDetectorRef,
        private _dialogRef: MatDialogRef<SampleFormDialog>,
        private _snackbar: Snackbar,
        private _dialog: Dialog,
        private _storageRegimeService: StorageRegimeService,
        private _customProtocolService: CustomProtocolService,
        private _evaluationService: EvaluationService,
        private _scheduledActionService: ScheduledActionService,
        @Inject(MAT_DIALOG_DATA) public data: SampleFormDialogData
    ) { }

    get showStorageRegimePicker(): boolean {
        return !this.data.key
            && !this.data.defaults?.massAction
            && this.typeControl.value === SampleType.HARVEST;
    }


    ngOnInit() {

        if (!this.data.key) {

            this.formGroup.get('site').valueChanges.subscribe(site => {
                if (site && this.formGroup.get('type').value == 'plant'){
                    this.formGroup.get('rowDistance').setValue(site.rowDistance);
                    this.formGroup.get('colDistance').setValue(site.colDistance);
                }
            });

            if (this.data.defaults.massAction) {
                let initForms: InitNewSampleForm[] = [];

                for (let i = 0; i < this.data.defaults.plantSamples.length; i++) {
                    let dataList = (JSON.parse(JSON.stringify(this.data)));
                    dataList.defaults.plantSample = this.data.defaults.plantSamples[i];
                    initForms.push(new InitNewSampleForm(dataList.defaults));
                }

                this._store.dispatch(initForms);

            } else this._store.dispatch(new InitNewSampleForm(this.data.defaults));

        } else this._store.dispatch(new InitUpdateSampleForm(this.data.key, this.data.clone));

        this.connectPlantSampleControl
            .valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(connect => {
                this.updateValidation();
            });

        this.storageRegimeControl
            .valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this.updateValidation();
            });

        this.storageRegimeControl.disable({ emitEvent: false });

        this.scionCultivarControl
            .valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(cultivar => this.onScionCultivarChanged(cultivar));

        this.state$.pipe(takeUntil(this._destroy$))
            .subscribe(state => {

                if (state.status === Status.COMPLETE) {
                    this.afterSampleCreated(state.data);
                } else if (this._scheduling) {
                    // Stays locked until the schedule has been saved.
                } else if (state.status !== Status.LOADING) {
                    this.restoreFormState();
                } else {
                    this.formGroup.disable();
                }
            });

        this.data$.pipe(takeUntil(this._destroy$))
            .subscribe(data => {
                if (data) this.reset(data);
            });

    }

    /**
     * After a successful Sample create/update: if a Storage Regime was
     * selected (new, non-mass-action harvest Sample only), generate and
     * submit its Evaluations/Scheduled Actions before closing — two
     * sequential calls after Sample creation, matching the app's existing
     * sequential-not-atomic pattern (see story Dev Notes), not a new
     * combined-request design.
     */
    // The index reloads once the schedule is saved, so its evaluation counts include the new rows.
    private closeAfterSchedule(sample: Partial<Sample>) {
        this._scheduling = false;
        this._dialogRef.disableClose = false;
        this._store.dispatch(new LoadSampleIndex());
        this._dialogRef.close(sample);
    }

    // Re-enables every control except the storage regime, which stays off until a cultivar with a crop is chosen.
    private restoreFormState() {
        Object.values(this.formGroup.controls)
            .filter(control => control !== this.storageRegimeControl)
            .forEach(control => control.enable({ emitEvent: false }));

        if (!this._orgKey || !this.scionCultivarControl.value?.cropId) {
            this.storageRegimeControl.disable({ emitEvent: false });
        } else {
            this.storageRegimeControl.enable({ emitEvent: false });
        }
    }

    private afterSampleCreated(sample: Partial<Sample>) {

        // A second COMPLETE emission must not schedule the sample again.
        if (this._scheduling) return;

        // Read from what was captured at save time, not from controls that may have been reset since.
        const regime = this._scheduledRegime;

        if (!regime || !this.showStorageRegimePicker) {
            this.closeAfterSchedule(sample);
            return;
        }

        this._scheduling = true;
        this._dialogRef.disableClose = true;

        const startDate = sample.birthDate || moment();

        // Stage order here must match evaluationsFromStorageRegime()'s own stage.map() order —
        // both read regime.config.evaluations directly, with no filtering, so the Nth created
        // evaluation always corresponds to the Nth stage.
        const stages = regime.config?.evaluations || [];

        const evaluations = evaluationsFromStorageRegime(
            sample.key,
            regime,
            this._scheduledProtocolId,
            startDate,
            sample.size ?? 1,
        );

        const submitEvaluations$ = evaluations.length
            ? this._evaluationService.createMany({ evals: evaluations })
            : of([] as Partial<Evaluation>[]);

        submitEvaluations$.pipe(
            switchMap(createdEvaluations => {
                // Server now preserves submission order (SampleEvaluationController::createMany()) —
                // zip the two same-order, same-length arrays into the stageKey->evalKey map
                // the two-pass Scheduled Action resolver needs.
                const evaluationsByStageKey: Record<string, string> = {};

                (createdEvaluations || []).forEach((evalu, index) => {
                    const stage = stages[index] as CustomStorageRegimeStage | undefined;
                    if (stage?.stageKey && evalu?.key) evaluationsByStageKey[stage.stageKey] = evalu.key;
                });

                const scheduledActions = scheduledActionsFromStorageRegime(sample.key, regime, evaluationsByStageKey, startDate);

                return scheduledActions.length
                    ? this._scheduledActionService.createMany({ actions: scheduledActions })
                    : of(null);
            }),
        ).subscribe({
            next: () => this.closeAfterSchedule(sample),
            error: () => {
                this._snackbar.error('Sample created, but its schedule could not be fully saved. Check its evaluations before adding any manually.');
                this.closeAfterSchedule(sample);
            }
        });
    }

    private loadRegimeOptions(orgKey: string, cropId: string) {
        this._optionsRequests.push(
            this._storageRegimeService.list(orgKey, cropId)
                .pipe(takeUntil(this._destroy$))
                .subscribe({
                    next: regimes => {
                        this.storageRegimeOptions = regimes;
                        this._changeDetector.markForCheck();
                    },
                    error: () => {
                        this._snackbar.error('Storage regimes could not be loaded. Try again.');
                        this._changeDetector.markForCheck();
                    },
                })
        );

        this._optionsRequests.push(
            this._customProtocolService.allProtocolsObserved(orgKey, [], { sampleTypes: [SampleType.HARVEST], crops: [cropId] })
                .pipe(takeUntil(this._destroy$))
                .subscribe({
                    next: protocols => {
                        this.protocolOptions = protocols;
                        this._changeDetector.markForCheck();
                    },
                    error: () => {
                        this._snackbar.error('Evaluation protocols could not be loaded. Try again.');
                        this._changeDetector.markForCheck();
                    },
                })
        );
    }

    reset(model: Partial<Sample>) {
        this._loadedCropId = null;
        this._optionsRequests.forEach(request => request.unsubscribe());
        this._optionsRequests = [];

        let data = {
            type: model.type,
            label: model.label || null,
            size: "size" in model ? model.size : null,
            description: model.description || null,

            birthDate: model.birthDate || moment().format('YYYY-MM-DD'),
            deathDate: model.deathDate || null,

            site: model.site || null,
            scionCultivar: model.scionCultivar || null,
            rootstockCultivar: model.rootstockCultivar || null,
            plantSample: model.plantSample || null,

            rowIndex: "rowIndex" in model ? model.rowIndex : null,
            positionIndex: "positionIndex" in model ? model.positionIndex : null,
            rowDistance: "rowDistance" in model ? model.rowDistance : null,
            colDistance: "colDistance" in model ? model.colDistance : null,

            ownerOrgKey: model.ownerOrgKey,
            _connectPlantSample: !!model.plantSample,

            storageRegime: null,
            protocolId: null,
        };

        this._orgKey = model.ownerOrgKey || null;
        this.formGroup.reset(data);
        this.updateValidation();

    }

    private onScionCultivarChanged(cultivar: any) {
        const cropId: string | null = cultivar?.cropId ?? null;

        // Disabling or enabling the form also emits this event, so only a real crop change may clear the choices.
        if (cropId === this._loadedCropId) return;
        this._loadedCropId = cropId && this._orgKey ? cropId : null;

        this.storageRegimeOptions = [];
        this.protocolOptions = [];
        this.storageRegimeControl.setValue(null, { emitEvent: false });
        this.protocolControl.setValue(null, { emitEvent: false });
        this.updateValidation();

        this._optionsRequests.forEach(request => request.unsubscribe());
        this._optionsRequests = [];

        if (!cropId || !this._orgKey) {
            this.storageRegimeControl.disable({ emitEvent: false });
            return;
        }

        this.storageRegimeControl.enable({ emitEvent: false });
        this.loadRegimeOptions(this._orgKey, cropId);
    }

    private updateValidation(){

        if(this.connectPlantSampleControl.value){
            this.scionCultivarControl.setValidators([]);
            this.siteControl.setValidators([]);
            this.plantSampleControl.setValidators([Validators.required]);
        }else{
            this.scionCultivarControl.setValidators([Validators.required]);
            this.siteControl.setValidators([Validators.required]);
            this.plantSampleControl.setValidators([]);
        }

        // Protocol is only required once a Storage Regime has actually been
        // selected — the regime itself stays fully optional.
        this.protocolControl.setValidators(
            this.storageRegimeControl.value ? [Validators.required] : []
        );

        // No emitEvent: the cultivar's own valueChanges calls back into this method, which would loop.
        this.scionCultivarControl.updateValueAndValidity({ emitEvent: false });
        this.siteControl.updateValueAndValidity();
        this.plantSampleControl.updateValueAndValidity();
        this.protocolControl.updateValueAndValidity();
    }

    attempt() {

        if (this._scheduling) return;

        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();

        if (this.formGroup.valid) {
            this.save();
        } else {
            console.warn('SampleFormDialog: form invalid', this.formGroup.value);
            this._snackbar.error("Invalid input. Check your input and try again.");
        }

    }

    save() {
        const form = this.formGroup.value;

        this._scheduledRegime = this.showStorageRegimePicker ? this.storageRegimeControl.value : null;
        this._scheduledProtocolId = this.protocolControl.value;

        const data: Partial<Sample> = {
            type            : form.type,
            birthDate       : coerseDateProperty(form.birthDate),
            deathDate       : coerseDateProperty(form.deathDate),
            label           : form.label,
            description     : form.description,
            size            : coerceNumberProperty(form.size, null),
            rowDistance     : coerceNumberProperty(form.rowDistance, null),
            colDistance     : coerceNumberProperty(form.colDistance, null),
            rowIndex        : coerceNumberProperty(form.rowIndex, null),
            positionIndex   : coerceNumberProperty(form.positionIndex, null),
            ownerOrgKey     : form.ownerOrgKey,
        }

        // Snapshot written directly, no interim dual-write — only
        // ever set on a new, non-mass-action harvest Sample (the
        // showStorageRegimePicker gate); the regime itself is fully
        // optional, so omit the key entirely rather than send null.
        if (this._scheduledRegime) {
            data.appliedStorageRegime = this._scheduledRegime.config;
        }

        if(form._connectPlantSample){
            data.plantSampleKey = form.plantSample.key;
            data.siteKey = null;
            data.scionCultivarKey = null;
            data.rootstockCultivarKey = null;
        } else {
            data.plantSampleKey = null;
            data.siteKey = form.site ? form.site.key : null;
            data.scionCultivarKey = form.scionCultivar ? form.scionCultivar.key : null;
            data.rootstockCultivarKey = form.rootstockCultivar ? form.rootstockCultivar.key : null;
        }

        if (this.data.key || !this.data.defaults.massAction) {
            this._store.dispatch(new SubmitSampleForm(data));
        } else {
            let harvestList:SubmitSampleForm[] = []

            for (let i = 0; i < this.data.defaults.plantSamples.length; i++) {
                const dataMassAction: Partial<Sample> = {
                    type            : form.type,
                    birthDate       : coerseDateProperty(form.birthDate),
                    deathDate       : coerseDateProperty(form.deathDate),
                    label           : form.label,
                    description     : form.description,
                    size            : coerceNumberProperty(form.size, null),
                    rowDistance     : coerceNumberProperty(form.rowDistance, null),
                    colDistance     : coerceNumberProperty(form.colDistance, null),
                    rowIndex        : coerceNumberProperty(form.rowIndex, null),
                    positionIndex   : coerceNumberProperty(form.positionIndex, null),
                    ownerOrgKey     : form.ownerOrgKey,
                    plantSampleKey  : this.data.defaults.plantSamples[i].key,
                }

                harvestList[i] = new SubmitSampleForm(dataMassAction);
            }

            this._store.dispatch(harvestList);
        }
    }

    cancel() {
        this._dialogRef.close();
    }

    addScionCultivar(event: MouseEvent) {

        event.stopPropagation();

        const data: CultivarFormDialogData = {
            defaults: {
                ownerOrgKey: this.ownerOrgKeyControl.value,
            }
        };
        this._dialog.open(CultivarFormDialog, { data })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe(result => {
                if (result) {
                    this.scionCultivarControl.setValue(result);
                }
            });
    }

    removeScionCultivar(event: MouseEvent){
        event.stopPropagation();
        this.scionCultivarControl.setValue(null);
    }

    addRootstockCultivar(event: MouseEvent) {

        event.stopPropagation();

        const data: CultivarFormDialogData = {
            defaults: {
                ownerOrgKey: this.ownerOrgKeyControl.value,
                isRootstock: true,
            }
        };
        this._dialog.open(CultivarFormDialog, { data })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe(result => {
                if (result) {
                    this.rootstockCultivarControl.setValue(result);
                }
            });
    }

    removeRootstockCultivar(event: MouseEvent){
        event.stopPropagation();
        this.rootstockCultivarControl.setValue(null);
    }

    addSite(event: MouseEvent) {

        event.stopPropagation();

        const data: SiteFormDialogData = {
            defaults: {
                ownerOrgKey: this.ownerOrgKeyControl.value,
            }
        };
        this._dialog.open(SiteFormDialog, { data })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe(result => {
                if (result) {
                    this.siteControl.setValue(result);
                }
            });
    }

    removeSite(event: MouseEvent){
        event.stopPropagation();
        this.siteControl.setValue(null);
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
